import React, { useEffect, useState } from 'react';
import { Alert, AlertIcon, AspectRatio, Box, Heading, Link, Text } from '@chakra-ui/react';
import {
    Recording,
    fetchRecording,
    formatFileSize,
    isMatchId,
    recordingDownloadUrl,
    recordingStreamUrl
} from '../../services/videoService';

interface RecordingPanelProps {
    /** The event's providerEventId — the storage box uses it verbatim as the matchId. */
    matchId?: string | null;
    title?: string;
    /** ISO start time; a game that has not started cannot have a recording yet. */
    startTime?: string;
}

type LookupState =
    | { status: 'loading' }
    | { status: 'found'; recording: Recording }
    | { status: 'missing' }
    | { status: 'error'; message: string };

const hasStarted = (startTime?: string): boolean => {
    if (!startTime) {
        return true;
    }
    const startedAt = new Date(startTime).getTime();
    return Number.isNaN(startedAt) || startedAt <= Date.now();
};

const useRecordingLookup = (matchId: string | null | undefined, enabled: boolean): LookupState => {
    const [state, setState] = useState<LookupState>({ status: 'loading' });

    useEffect(() => {
        if (!enabled || !matchId) {
            return;
        }
        const controller = new AbortController();
        setState({ status: 'loading' });

        fetchRecording(matchId, controller.signal)
            .then(recording => setState(recording ? { status: 'found', recording } : { status: 'missing' }))
            .catch(error => {
                if (controller.signal.aborted) {
                    return;
                }
                setState({ status: 'error', message: error?.message || 'Recording lookup failed' });
            });

        return () => controller.abort();
    }, [matchId, enabled]);

    return state;
};

/**
 * Recordings land on the storage box roughly an hour after a game starts — measured at
 * 64-66 minutes across streams. Until then "no recording" means "not yet", and saying so
 * stops traders from reporting a bug every time a game ends.
 */
const UPLOAD_LAG_MS = 90 * 60 * 1000;

const missingText = (startTime?: string): string => {
    const startedAt = startTime ? new Date(startTime).getTime() : Number.NaN;
    if (!Number.isNaN(startedAt) && Date.now() - startedAt < UPLOAD_LAG_MS) {
        return 'Recording not uploaded yet — usually ready about an hour after the game starts';
    }
    return 'No recording available';
};

const downloadLabel = (recording: Recording): string => {
    const size = formatFileSize(recording.size);
    return size ? `Download (${size})` : 'Download';
};

const RecordingVideo: React.FC<{ recording: Recording; title: string }> = ({ recording, title }) => {
    const [playbackFailed, setPlaybackFailed] = useState(false);

    if (playbackFailed) {
        return (
            <Alert status="info" borderRadius="md" fontSize="xs" py={2}>
                <AlertIcon />
                This browser cannot play {recording.contentType} — use the download link.
            </Alert>
        );
    }

    return (
        <AspectRatio ratio={16 / 9} maxW="100%">
            {/* src on the element rather than a <source> child: only the former fires
                `error` on the video itself when the container is unsupported, which is
                how Safari and Firefox reach the download fallback above. */}
            <video
                key={recording.matchId}
                title={title}
                src={recordingStreamUrl(recording.matchId)}
                controls
                preload="metadata"
                playsInline
                onError={() => setPlaybackFailed(true)}
                style={{ width: '100%', height: '100%', backgroundColor: '#000' }}
            />
        </AspectRatio>
    );
};

interface LookupResultProps {
    state: LookupState;
    title: string;
    startTime?: string;
}

const LookupResult: React.FC<LookupResultProps> = ({ state, title, startTime }) => {
    if (state.status === 'loading') {
        return <Text fontSize="xs" color="gray.500">Looking for recording…</Text>;
    }
    if (state.status === 'missing') {
        return <Text fontSize="xs" color="gray.500">{missingText(startTime)}</Text>;
    }
    if (state.status === 'error') {
        return (
            <Alert status="warning" borderRadius="md" fontSize="xs" py={2}>
                <AlertIcon />
                {state.message}
            </Alert>
        );
    }

    return (
        <>
            <RecordingVideo recording={state.recording} title={title} />
            <Link
                href={recordingDownloadUrl(state.recording.matchId)}
                download={state.recording.filename}
                fontSize="xs"
                color="blue.500"
                mt={1}
                display="inline-block"
            >
                {downloadLabel(state.recording)}
            </Link>
        </>
    );
};

/**
 * Takes over from the live-stream box once a game is over: plays the recording held
 * on the storage box and always offers it as a download, because the files are
 * Matroska and not every browser can play that container inline.
 */
export const RecordingPanel: React.FC<RecordingPanelProps> = ({ matchId, title = 'Match recording', startTime }) => {
    const eligible = isMatchId(matchId) && hasStarted(startTime);
    const state = useRecordingLookup(matchId, eligible);

    if (!eligible) {
        return null;
    }

    return (
        <Box width={{ base: '100%', md: '300px' }}>
            <Heading size="xs" mb={1}>Recording</Heading>
            <LookupResult state={state} title={title} startTime={startTime} />
        </Box>
    );
};
